import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  ClipboardList,
  PlayCircle,
  FileText,
  AlertTriangle,
  CheckCircle2,
  FileBarChart,
  Users,
  Building2,
  Settings,
  LogOut,
  Bell,
  Menu,
  X,
  ChevronDown,
  Shield,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_compliance-check-85/artifacts/kqzoobkc_REVYNIQX_TM-01.png';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notifications] = useState([]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getNavItems = () => {
    const baseItems = [
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['Admin', 'Auditor', 'SPOC', 'Management'] },
    ];

    const auditItems = [
      { path: '/audits', icon: ClipboardList, label: 'Audit Planning', roles: ['Admin', 'Auditor'] },
      { path: '/audit-execution', icon: PlayCircle, label: 'Audit Execution', roles: ['Admin', 'Auditor', 'SPOC'] },
      { path: '/evidence', icon: FileText, label: 'Evidence', roles: ['Admin', 'Auditor', 'SPOC'] },
    ];

    const findingsItems = [
      { path: '/findings', icon: AlertTriangle, label: 'Findings', roles: ['Admin', 'Auditor', 'SPOC'] },
      { path: '/capa', icon: CheckCircle2, label: 'CAPA Tracking', roles: ['Admin', 'Auditor', 'SPOC'] },
    ];

    const reportItems = [
      { path: '/reports', icon: FileBarChart, label: 'Reports', roles: ['Admin', 'Auditor', 'Management'] },
    ];

    const adminItems = [
      { path: '/users', icon: Users, label: 'User Management', roles: ['Admin'] },
      { path: '/departments', icon: Building2, label: 'Departments', roles: ['Admin'] },
      { path: '/controls', icon: Shield, label: 'ISO Controls', roles: ['Admin'] },
    ];

    return { baseItems, auditItems, findingsItems, reportItems, adminItems };
  };

  const { baseItems, auditItems, findingsItems, reportItems, adminItems } = getNavItems();

  const filterByRole = (items) => items.filter(item => item.roles.includes(user?.role));

  const NavItem = ({ item }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;

    return (
      <Link
        to={item.path}
        className={`nav-item ${isActive ? 'active' : ''}`}
        data-testid={`nav-${item.path.replace('/', '')}`}
      >
        <Icon size={20} />
        <span>{item.label}</span>
      </Link>
    );
  };

  const NavSection = ({ label, items }) => {
    const filteredItems = filterByRole(items);
    if (filteredItems.length === 0) return null;

    return (
      <div className="mb-4">
        <div className="nav-label">{label}</div>
        {filteredItems.map(item => (
          <NavItem key={item.path} item={item} />
        ))}
      </div>
    );
  };

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className={`sidebar ${!sidebarOpen ? 'sidebar-collapsed' : ''}`}>
        <div className="logo-container">
          <img src={LOGO_URL} alt="REVYNIQX" className="logo-image" />
        </div>

        <nav className="nav-section flex-1 overflow-y-auto">
          <NavSection label="Overview" items={baseItems} />
          <NavSection label="Audit" items={auditItems} />
          <NavSection label="Findings & CAPA" items={findingsItems} />
          <NavSection label="Reports" items={reportItems} />
          <NavSection label="Administration" items={adminItems} />
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-3 py-2">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-[#4A5690] text-white text-sm">
                {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.role}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`main-content ${!sidebarOpen ? 'main-content-expanded' : ''}`}>
        {/* Header */}
        <header className="header">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="toggle-sidebar-btn"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {location.pathname === '/dashboard' && 'Dashboard'}
                {location.pathname === '/audits' && 'Audit Planning'}
                {location.pathname === '/audit-execution' && 'Audit Execution'}
                {location.pathname === '/evidence' && 'Evidence Management'}
                {location.pathname === '/findings' && 'Findings Management'}
                {location.pathname === '/capa' && 'CAPA Tracking'}
                {location.pathname === '/reports' && 'Reports'}
                {location.pathname === '/users' && 'User Management'}
                {location.pathname === '/departments' && 'Departments'}
                {location.pathname === '/controls' && 'ISO Controls'}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                  <Bell size={20} />
                  {notifications.length > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-[#E89A2E]">
                      {notifications.length}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-sm text-slate-500">
                    No new notifications
                  </div>
                ) : (
                  notifications.map((n, i) => (
                    <DropdownMenuItem key={i}>
                      <div className="flex flex-col gap-1">
                        <span className="font-medium">{n.title}</span>
                        <span className="text-xs text-slate-500">{n.message}</span>
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-[#4A5690] text-white text-xs">
                      {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <ChevronDown size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user?.name}</span>
                    <span className="text-xs font-normal text-slate-500">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <Settings size={16} className="mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut size={16} className="mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 bg-[#F8FAFC] min-h-[calc(100vh-65px)]">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
